import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  Avatar,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Collapse,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import axios from "axios";
import { Notify } from "notiflix";
import { ENDPOINT } from "constants";
import { useSelector } from "react-redux";
import usePermission from "hooks/usePermission";

const API_BASE = `${ENDPOINT}api/v1/admin/tickets`;
const ELEVET_BASE = `${ENDPOINT}api/v1/admin/elevet`;

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatTime = (iso) => {
  if (!iso) return "";
  const d = new Date(typeof iso === "string" ? iso.replace(" ", "T") : iso);
  return isNaN(d.getTime()) ? "" : d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const formatDetailDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(typeof iso === "string" ? iso.replace(" ", "T") : iso);
  return isNaN(d.getTime())
    ? "—"
    : d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const getFileType = (url) => {
  const ext = url?.split(".").pop()?.toLowerCase();
  if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) return "img";
  if (ext === "pdf") return "pdf";
  return "file";
};

const attachmentDownloadUrl = (url) => {
  if (!url) return "#";
  return `${ENDPOINT}api/v1/download?url=${encodeURIComponent(url)}`;
};

const attachmentFileName = (url) => {
  if (!url) return "attachment";
  const raw = decodeURIComponent(url.split("/").pop().split("?")[0]);
  return raw.replace(/^\d+_/, "");
};

const normalizeRawMessage = (raw) => {
  const isAdmin = raw.sender === "admin";
  const senderObj = isAdmin ? raw.adminSender : raw.userSender;
  return {
    id: raw.id,
    message: raw.message,
    attachmentUrl: raw.attachmentUrl,
    createdAt: raw.createdAt,
    sender: {
      id: senderObj?.id,
      name: isAdmin ? senderObj?.name : senderObj?.firstName,
      email: senderObj?.email,
      type: raw.sender,
    },
  };
};

const STATUS_COLORS = {
  open:        { color: "#1e40af", bg: "#dbeafe", border: "#93c5fd" },
  in_progress: { color: "#92400e", bg: "#fef3c7", border: "#fcd34d" },
  pending:     { color: "#7c3aed", bg: "#ede9fe", border: "#c4b5fd" },
  resolved:    { color: "#166534", bg: "#dcfce7", border: "#86efac" },
  closed:      { color: "#374151", bg: "#f3f4f6", border: "#d1d5db" },
};

const STATUS_DISPLAY = {
  open:        "Open",
  in_progress: "Active",
  pending:     "Processing",
  resolved:    "Resolved",
  closed:      "Resolved",
};

// ─── Sidebar Section ──────────────────────────────────────────────────────────

const SidebarSection = ({ title, open, onToggle, children }) => (
  <Box sx={{ borderBottom: "1px solid #e5e7eb", "&:last-child": { borderBottom: "none" } }}>
    <Box
      onClick={onToggle}
      sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 1.75, bgcolor: "#3d4fd6", cursor: "pointer", userSelect: "none" }}
    >
      <Typography sx={{ color: "#fff", fontSize: 14, fontWeight: 600 }}>{title}</Typography>
      {open
        ? <ExpandLessIcon sx={{ color: "#fff", fontSize: 20 }} />
        : <ExpandMoreIcon sx={{ color: "#fff", fontSize: 20 }} />}
    </Box>
    <Collapse in={open}>
      <Box sx={{ bgcolor: "#fff", p: "12px 16px" }}>{children}</Box>
    </Collapse>
  </Box>
);

// ─── Detail Row ───────────────────────────────────────────────────────────────

const DetailRow = ({ label, value }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1, py: 1, borderBottom: "1px solid #f3f4f6", "&:last-child": { borderBottom: "none" } }}>
    <Typography sx={{ fontSize: 12, color: "#6b7280", flexShrink: 0, minWidth: 90 }}>{label}</Typography>
    <Box sx={{ fontSize: 12, color: "#111", fontWeight: 500, textAlign: "right", wordBreak: "break-word" }}>{value || "—"}</Box>
  </Box>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const TicketDetail = ({ ticket, onBack }) => {
  const { user } = useSelector((state) => state.auth);
  const token = user?.accessToken;
  const authHeader = { Authorization: `Bearer ${token}` };
  const canCreate = usePermission("tickets", "create");
  const canUpdate = usePermission("tickets", "update");

  const [ticketData, setTicketData] = useState(ticket);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [attachedFile, setAttachedFile] = useState(null);
  const [showAllFiles, setShowAllFiles] = useState(false);
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);

  // Sidebar section open states
  const [detailsOpen, setDetailsOpen] = useState(true);
  const [elevetOpen, setElevetOpen] = useState(false);
  const [filesOpen, setFilesOpen] = useState(true);

  // Move-to-Elevet state
  const isServiceTicket = ticketData.ticketType === "Service";
  const alreadyMoved = !!ticketData.elevetId;
  const [elevetLoading, setElevetLoading] = useState(false);
  const [elevetOrgId, setElevetOrgId] = useState(null);
  // Service ticket state
  const [elevetProjects, setElevetProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [selectedServiceId, setSelectedServiceId] = useState("");
  // Non-service ticket state
  const [elevetDepts, setElevetDepts] = useState([]);
  const [selectedDeptId, setSelectedDeptId] = useState("");
  const [isMoving, setIsMoving] = useState(false);

  // Load Elevet data when section is opened for the first time
  useEffect(() => {
    const alreadyLoaded = isServiceTicket ? elevetProjects.length > 0 : elevetDepts.length > 0;
    if (!elevetOpen || alreadyMoved || alreadyLoaded) return;
    const load = async () => {
      setElevetLoading(true);
      try {
        if (isServiceTicket) {
          // Service: get client data (org_id + projects) by email
          const res = await axios.post(`${ELEVET_BASE}/client-by-email`, { email: ticketData.user?.email }, { headers: authHeader });
          const clientData = res.data?.message?.data || res.data?.data || {};
          setElevetProjects(clientData.projects || []);
          setElevetOrgId(clientData.client?.org_id || null);
        } else {

          const [clientRes, deptsRes] = await Promise.allSettled([
            axios.post(`${ELEVET_BASE}/client-by-email`, { email: ticketData.user?.email }, { headers: authHeader }),
            axios.get(`${ELEVET_BASE}/all-departments`, { headers: authHeader }),
          ]);

          if (clientRes.status === "fulfilled") {
            const clientData = clientRes.value.data?.message?.data || clientRes.value.data?.data || {};
            setElevetOrgId(clientData.client?.org_id || null);
          } else {
            console.error("Elevet client-by-email lookup failed:", clientRes.reason);
            Notify.warning("Could not find this requester's Elevet client record — org ID will be missing.");
          }

          if (deptsRes.status === "fulfilled") {
            const depts = deptsRes.value.data?.message?.data || deptsRes.value.data?.data || [];
            setElevetDepts(Array.isArray(depts) ? depts : []);
          } else {
            console.error("Elevet all-departments fetch failed:", deptsRes.reason);
            Notify.failure("Failed to load Elevet departments list");
          }
        }
      } catch {
        Notify.failure("Failed to load Elevet data");
      } finally {
        setElevetLoading(false);
      }
    };
    load();
  }, [elevetOpen]);

  // Service ticket derived values
  const selectedProject = elevetProjects.find((p) => String(p.id) === String(selectedProjectId));
  const serviceOptions = selectedProject?.services || [];
  const selectedService = serviceOptions.find((s) => String(s.id) === String(selectedServiceId));
  const autoDept = selectedService?.service?.department;

  const handleMoveToElevet = async () => {
    if (isServiceTicket) {
      if (!selectedProjectId) { Notify.warning("Please select a project"); return; }
      if (!selectedServiceId) { Notify.warning("Please select a service"); return; }
      if (!autoDept) { Notify.warning("Selected service has no department"); return; }
    } else {
      if (!selectedDeptId) { Notify.warning("Please select a department"); return; }
    }
    setIsMoving(true);
    try {
      let payload;
      if (isServiceTicket) {
        payload = {
          org_id: elevetOrgId,
          department_id: autoDept.id,
          department_name: autoDept.name,
          project_id: selectedProjectId,
          project_service_id: selectedService?.id,
        };
      } else {
        const dept = elevetDepts.find((d) => String(d.id) === String(selectedDeptId));
        // Each department from /all-departments already carries its own
        // org_id — use that directly instead of depending on the separate
        // client-by-email lookup (elevetOrgId), which can fail/be unavailable
        // independently of the departments list itself.
        payload = {
          org_id: dept?.org_id ?? elevetOrgId,
          department_id: selectedDeptId,
          department_name: dept?.name || "",
        };
      }
      const { data } = await axios.post(`${API_BASE}/${ticket.id}/move-to-elevet`, payload, { headers: authHeader });
      if (data?.success) {
        const deptName = isServiceTicket ? autoDept.name : (elevetDepts.find((d) => String(d.id) === String(selectedDeptId))?.name || "");
        setTicketData((prev) => ({
          ...prev,
          elevetId: data.message?.elevetId,
          department: data.message?.department || deptName,
        }));
        Notify.success("Ticket moved to Elevet!");
      }
    } catch (err) {
      Notify.failure(err?.response?.data?.message || "Failed to move ticket to Elevet");
    } finally {
      setIsMoving(false);
    }
  };

  // ── Messages ──────────────────────────────────────────────────────────────

  const fetchMessages = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_BASE}/${ticket.id}/messages`, { headers: authHeader });
      if (data?.success) setMessages(data?.message?.messages || []);
    } catch (err) {
      console.error(err);
    }
  }, [ticket.id, token]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendReply = async () => {
    if (!reply.trim() && !attachedFile) return;
    if (isSending) return;
    const text = reply.trim();
    setReply("");
    setIsSending(true);
    try {
      let data;
      if (attachedFile) {
        const form = new FormData();
        if (text) form.append("message", text);
        form.append("attachment", attachedFile);
        const res = await axios.post(`${API_BASE}/${ticket.id}/messages`, form, {
          headers: { ...authHeader, "Content-Type": "multipart/form-data" },
        });
        data = res.data;
      } else {
        const res = await axios.post(`${API_BASE}/${ticket.id}/messages`, { message: text }, { headers: authHeader });
        data = res.data;
      }
      setAttachedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      if (data?.message) setMessages((prev) => [...prev, normalizeRawMessage(data.message)]);
      else fetchMessages();
    } catch {
      Notify.failure("Failed to send reply");
      setReply(text);
    } finally {
      setIsSending(false);
    }
  };

  const handleReplyKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey && (reply.trim() || attachedFile)) {
      e.preventDefault();
      sendReply();
    }
  };

  // ── Ticket actions ────────────────────────────────────────────────────────

  const updateStatus = async (status) => {
    try {
      const { data } = await axios.patch(`${API_BASE}/${ticket.id}`, { status }, { headers: authHeader });
      if (data?.success) {
        setTicketData((prev) => ({ ...prev, status }));
        Notify.success("Status updated");
      }
    } catch {
      Notify.failure("Failed to update status");
    }
  };

  const handleAction = async (action) => {
    try {
      await axios.patch(`${API_BASE}/${ticket.id}/${action}`, {}, { headers: authHeader });
      setTicketData((prev) => ({ ...prev, status: action === "close" ? "closed" : "open" }));
      Notify.success(`Ticket ${action}d`);
    } catch {
      Notify.failure(`Failed to ${action} ticket`);
    }
  };

  // ── Computed ──────────────────────────────────────────────────────────────

  const sharedFiles = messages
    .filter((m) => m.attachmentUrl)
    .map((m) => ({
      url: m.attachmentUrl,
      name: attachmentFileName(m.attachmentUrl),
      type: getFileType(m.attachmentUrl),
      time: formatTime(m.createdAt),
    }));

  const statusKey = (ticketData.status || "open").toLowerCase().replace(" ", "_");
  const statusColors = STATUS_COLORS[statusKey] || STATUS_COLORS.open;
  const userInitials = (ticketData.user?.firstName || "U")[0].toUpperCase();
  const hasInput = reply.trim() || attachedFile;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={3} pb={3}>
        <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start", height: "calc(100vh - 200px)" }}>

          {/* ══════════════ LEFT: CHAT ══════════════ */}
          <Box sx={{
            flex: 1, display: "flex", flexDirection: "column",
            bgcolor: "#fff", border: "1px solid #e5e7eb", borderRadius: "12px",
            overflow: "hidden", minWidth: 0, height: "100%",
          }}>

            {/* Chat Header */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2.5, py: "14px", borderBottom: "1px solid #e5e7eb" }}>
              <IconButton size="small" onClick={onBack} sx={{ color: "#374151", flexShrink: 0 }}>
                <ArrowBackIcon />
              </IconButton>
              <Avatar sx={{ bgcolor: "#155DFC", width: 40, height: 40, fontSize: 14, fontWeight: 700, flexShrink: 0, boxShadow: "0 2px 6px rgba(21,93,252,0.25)" }}>
                {userInitials}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 15, fontWeight: 600, color: "#111", lineHeight: 1.3 }}>
                  {ticketData.user?.firstName || "User"}
                </Typography>
                <Typography sx={{ fontSize: 12, color: "#6b7280" }}>
                  {ticketData.user?.email || "Support Requester"}
                </Typography>
              </Box>
            </Box>

            {/* Ticket Info Bar */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2.5, py: "10px", borderBottom: "1px solid #e5e7eb", bgcolor: "#fafafa", flexWrap: "wrap" }}>
              <Typography sx={{ fontSize: 13, color: "#1447E6" }}>
                Ticket:{" "}
                <Box component="span" sx={{ fontWeight: 600, color: "#3d4fd6" }}>
                  TKT - {ticketData.ticketIndex || ticketData.id?.slice(-6)}
                </Box>
              </Typography>
              <Box component="span" sx={{
                fontSize: 12, fontWeight: 500, px: 1.25, py: "2px", borderRadius: "20px",
                color: statusColors.color, bgcolor: statusColors.bg, border: `1px solid ${statusColors.border}`,
              }}>
                {STATUS_DISPLAY[(ticketData.status || "open").toLowerCase()] || ticketData.status}
              </Box>
              {ticketData.department && (
                <Typography sx={{ fontSize: 12, color: "#6b7280", ml: "auto" }}>
                  Dept: {ticketData.department}
                </Typography>
              )}
            </Box>

            {/* Messages */}
            <Box sx={{ flex: 1, overflowY: "auto", p: 2.5, display: "flex", flexDirection: "column", gap: 2.5, bgcolor: "#f8fafc" }}>
              {messages.length === 0 && (
                <Typography sx={{ textAlign: "center", color: "#9ca3af", fontSize: 14, mt: 4 }}>
                  No messages yet
                </Typography>
              )}
              {messages.map((m) => {
                const isAdmin = m.sender?.type === "admin";
                const name = m.sender?.name || (isAdmin ? "Support" : "User");
                const initials = name.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase();
                return (
                  <Box key={m.id} sx={{ display: "flex", flexDirection: "column", alignItems: isAdmin ? "flex-end" : "flex-start" }}>
                    <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1, flexDirection: isAdmin ? "row-reverse" : "row" }}>
                      <Avatar sx={{ width: 34, height: 34, bgcolor: isAdmin ? "#3d4fd6" : "#155DFC", fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
                        {initials}
                      </Avatar>
                      <Box sx={{ maxWidth: "65%" }}>
                        <Typography sx={{ fontSize: 13, color: "#6b7280", fontWeight: 500, display: "block", mb: 0.5, textAlign: isAdmin ? "right" : "left" }}>
                          {name}
                        </Typography>
                        <Box sx={{
                          px: "18px", py: "12px",
                          borderRadius: isAdmin ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
                          bgcolor: isAdmin ? "#3d4fd6" : "#fff",
                          border: isAdmin ? "none" : "1px solid #e5e7eb",
                          boxShadow: isAdmin ? "none" : "0 1px 4px rgba(0,0,0,0.06)",
                          color: isAdmin ? "#fff" : "#111827",
                          fontSize: 14, lineHeight: 1.65, wordBreak: "break-word",
                        }}>
                          {m.message}
                          {m.attachmentUrl && (
                            <Box
                              component="a"
                              href={attachmentDownloadUrl(m.attachmentUrl)}
                              download={attachmentFileName(m.attachmentUrl)}
                              sx={{ display: "block", fontSize: 12, mt: 0.5, color: isAdmin ? "rgba(255,255,255,0.8)" : "#3d4fd6", textDecoration: "underline", cursor: "pointer" }}
                            >
                              📎 {attachmentFileName(m.attachmentUrl)}
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </Box>
                    <Typography sx={{ fontSize: 11, color: "#9ca3af", display: "block", mt: 0.5, mx: "42px" }}>
                      {formatTime(m.createdAt)}
                    </Typography>
                  </Box>
                );
              })}
              <div ref={messagesEndRef} />
            </Box>

            {/* File Preview Strip */}
            {attachedFile && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 2, py: "6px", bgcolor: "#f0f4ff", borderTop: "1px solid #dce5fc" }}>
                <Typography sx={{ fontSize: 13, color: "#3d4fd6", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  📎 {attachedFile.name}
                </Typography>
                <IconButton size="small" onClick={() => { setAttachedFile(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}>
                  <CloseIcon sx={{ fontSize: 16, color: "#6b7280" }} />
                </IconButton>
              </Box>
            )}

            {/* Input Area */}
            {canCreate && (
              <>
                <input ref={fileInputRef} type="file" style={{ display: "none" }} onChange={(e) => setAttachedFile(e.target.files?.[0] || null)} />
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 2, py: 1.5, borderTop: "1px solid #e5e7eb", bgcolor: "#fff" }}>
                  <IconButton
                    size="small"
                    onClick={() => fileInputRef.current?.click()}
                    sx={{ color: "#6b7280", "&:hover": { color: "#3d4fd6" } }}
                  >
                    <AttachFileIcon sx={{ transform: "rotate(45deg)" }} />
                  </IconButton>
                  <TextField
                    fullWidth size="small" multiline maxRows={3}
                    placeholder="Type your reply…"
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    onKeyDown={handleReplyKeyDown}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: 14, "& fieldset": { borderColor: "#e5e7eb" }, "&:hover fieldset": { borderColor: "#3d4fd6" }, "&.Mui-focused fieldset": { borderColor: "#3d4fd6" } } }}
                  />
                  <IconButton
                    onClick={sendReply}
                    disabled={!hasInput || isSending}
                    sx={{
                      width: 44, height: 44, borderRadius: "14px", flexShrink: 0,
                      bgcolor: hasInput ? "#155DFC" : "#93A8F4",
                      color: "#fff",
                      transition: "background 0.2s",
                      "&:hover": { bgcolor: hasInput ? "#1247d6" : "#7b94f0" },
                      "&.Mui-disabled": { bgcolor: "#93A8F4", color: "#fff" },
                    }}
                  >
                    <SendIcon sx={{ transform: "rotate(-45deg)", fontSize: 18 }} />
                  </IconButton>
                </Box>
                <Typography sx={{ fontSize: 11, color: "#9ca3af", px: 2, pb: 1 }}>
                  Press Enter to send, Shift + Enter for new line
                </Typography>
              </>
            )}
          </Box>

          {/* ══════════════ RIGHT: SIDEBAR ══════════════ */}
          <Box sx={{ width: 300, flexShrink: 0, border: "1px solid #e5e7eb", borderRadius: "12px", overflow: "hidden", height: "100%", overflowY: "auto" }}>

            {/* ── Ticket Details ── */}
            <SidebarSection title="Ticket Details" open={detailsOpen} onToggle={() => setDetailsOpen((p) => !p)}>
              <DetailRow label="Requester" value={ticketData.user?.firstName} />
              <DetailRow label="Email" value={ticketData.user?.email} />
              <DetailRow
                label="Ticket ID"
                value={ticketData.elevetId ? `#${ticketData.elevetId}` : `#${ticketData.id?.slice(-6)}`}
              />
              <DetailRow label="Created" value={formatDetailDate(ticketData.createdAt)} />
              <DetailRow label="Last Update" value={formatDetailDate(ticketData.updatedAt)} />
              <DetailRow label="Type" value={ticketData.ticketType} />
              {ticketData.department && <DetailRow label="Department" value={ticketData.department} />}

              {/* Status selector */}
              <Box sx={{ pt: 1, pb: 0.5 }}>
                <Typography sx={{ fontSize: 12, color: "#6b7280", mb: 0.75 }}>Status</Typography>
                <FormControl size="small" fullWidth>
                  <Select
                    value={(ticketData.status || "open").toLowerCase()}
                    onChange={(e) => updateStatus(e.target.value)}
                    disabled={!canUpdate}
                    sx={{ fontSize: 12, borderRadius: "6px", "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e5e7eb" } }}
                  >
                    <MenuItem value="open" sx={{ fontSize: 12 }}>Open</MenuItem>
                    <MenuItem value="in_progress" sx={{ fontSize: 12 }}>Active</MenuItem>
                    <MenuItem value="pending" sx={{ fontSize: 12 }}>Pending</MenuItem>
                    <MenuItem value="resolved" sx={{ fontSize: 12 }}>Resolved</MenuItem>
                    <MenuItem value="closed" sx={{ fontSize: 12 }}>Closed</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {/* Close / Reopen */}
              {canUpdate && (
                <Box sx={{ pt: 1.5 }}>
                  {ticketData.status !== "closed" ? (
                    <MDButton color="error" size="small" onClick={() => handleAction("close")} style={{ width: "100%" }}>
                      Close Ticket
                    </MDButton>
                  ) : (
                    <MDButton color="success" size="small" onClick={() => handleAction("reopen")} style={{ width: "100%" }}>
                      Reopen Ticket
                    </MDButton>
                  )}
                </Box>
              )}
            </SidebarSection>

            {/* ── Move to Elevet ── */}
            <SidebarSection
              title={alreadyMoved ? "✓ Moved to Elevet" : "Move to Elevet"}
              open={elevetOpen}
              onToggle={() => setElevetOpen((p) => !p)}
            >
              {alreadyMoved ? (
                <>
                  {ticketData.elevetId && <DetailRow label="Elevet ID" value={`#${ticketData.elevetId}`} />}
                  {ticketData.department && <DetailRow label="Department" value={ticketData.department} />}
                  <DetailRow label="Service" value={ticketData.ticketType} />
                </>
              ) : elevetLoading ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1 }}>
                  <CircularProgress size={14} />
                  <Typography sx={{ fontSize: 12, color: "#6b7280" }}>Loading Elevet data…</Typography>
                </Box>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  {isServiceTicket ? (
                    <>
                      <FormControl size="small" fullWidth>
                        <InputLabel sx={{ fontSize: 12 }}>Project *</InputLabel>
                        <Select
                          value={selectedProjectId}
                          onChange={(e) => { setSelectedProjectId(e.target.value); setSelectedServiceId(""); }}
                          label="Project *"
                          sx={{ fontSize: 12 }}
                        >
                          {elevetProjects.map((p) => (
                            <MenuItem key={p.id} value={String(p.id)} sx={{ fontSize: 12 }}>
                              Project #{p.id}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <FormControl size="small" fullWidth disabled={!selectedProjectId}>
                        <InputLabel sx={{ fontSize: 12 }}>Service *</InputLabel>
                        <Select
                          value={selectedServiceId}
                          onChange={(e) => setSelectedServiceId(e.target.value)}
                          label="Service *"
                          sx={{ fontSize: 12 }}
                        >
                          {serviceOptions.map((s) => (
                            <MenuItem key={s.id} value={String(s.id)} sx={{ fontSize: 12 }}>
                              {s.service?.name || `Service #${s.id}`}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      {autoDept && (
                        <Box sx={{ px: 1, py: 0.75, bgcolor: "#f0fdf4", borderRadius: 1, border: "1px solid #bbf7d0" }}>
                          <Typography sx={{ fontSize: 11, color: "#166534" }}>
                            Department (auto): <strong>{autoDept.name}</strong>
                          </Typography>
                        </Box>
                      )}
                    </>
                  ) : (
                    <FormControl size="small" fullWidth>
                      <InputLabel sx={{ fontSize: 12 }}>Department *</InputLabel>
                      <Select
                        value={selectedDeptId}
                        onChange={(e) => setSelectedDeptId(e.target.value)}
                        label="Department *"
                        sx={{ fontSize: 12 }}
                      >
                        {elevetDepts.map((d) => (
                          <MenuItem key={d.id} value={String(d.id)} sx={{ fontSize: 12 }}>{d.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                  {canUpdate && (
                    <MDButton color="success" size="small" onClick={handleMoveToElevet} disabled={isMoving}>
                      {isMoving ? "Moving…" : "Move to Elevet"}
                    </MDButton>
                  )}
                </Box>
              )}
            </SidebarSection>

            {/* ── Files Shared ── */}
            <SidebarSection title="Files Shared" open={filesOpen} onToggle={() => setFilesOpen((p) => !p)}>
              {sharedFiles.length === 0 ? (
                <Typography sx={{ fontSize: 13, color: "#9ca3af", textAlign: "center", py: 1.5 }}>
                  No files shared yet
                </Typography>
              ) : (
                <>
                  {(showAllFiles ? sharedFiles : sharedFiles.slice(0, 2)).map((file, i) => (
                    <Box
                      key={i}
                      component="a"
                      href={attachmentDownloadUrl(file.url)}
                      download={file.name}
                      sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1.25, borderBottom: "1px solid #f3f4f6", textDecoration: "none", color: "inherit", "&:last-of-type": { borderBottom: "none" } }}
                    >
                      <Box sx={{
                        width: 40, height: 40, borderRadius: "6px", display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 10, fontWeight: 700, color: "#fff", flexShrink: 0,
                        bgcolor: file.type === "pdf" ? "#ef4444" : file.type === "img" ? "#3b82f6" : "#6b7280",
                      }}>
                        {file.type === "pdf" ? "PDF" : file.type === "img" ? "IMG" : "FILE"}
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 500, color: "#111", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {file.name}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: "#6b7280", mt: 0.25 }}>
                          Shared at {file.time}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                  {sharedFiles.length > 2 && (
                    <Box
                      component="button"
                      onClick={() => setShowAllFiles((p) => !p)}
                      sx={{ background: "none", border: "none", color: "#3d4fd6", fontSize: 13, fontWeight: 500, cursor: "pointer", pt: 1, "&:hover": { textDecoration: "underline" } }}
                    >
                      {showAllFiles ? "Show less" : `Show ${sharedFiles.length - 2} more`}
                    </Box>
                  )}
                </>
              )}
            </SidebarSection>

          </Box>
        </Box>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
};

export default TicketDetail;
